/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import LoginFields from '@amblique/king-living/app/components/forms/login-fields';
import {AlertErrorIcon} from '@amblique/king-living/app/components/icons';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  Stack,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import SocialLogin from '@amblique/king-living/app/components/social-login';
import StandardLogin from '@amblique/king-living/app/components/standard-login';
import {LOGIN_TYPES} from '@salesforce/retail-react-app/app/constants';

const PasswordlessLogin = ({
  email,
  form,
  handleBackToLanding,
  handleForgotPasswordClick,
  handlePasswordlessLoginClick,
  handleResendLink,
  idps = [],
  isPasswordlessEmailSent = false,
  isPasswordlessTokenInvalid = false,
  isSocialEnabled = false,
  setLoginType,
}) => {
  const [showPasswordView, setShowPasswordView] = useState(false);

  // Prefill email if provided from landing page
  useEffect(() => {
    if (email) {
      form.setValue('email', email);
    }
  }, [email, form]);

  const handlePasswordButton = async (e) => {
    setLoginType(LOGIN_TYPES.PASSWORD);
    const isValid = await form.trigger();
    const domForm = e.target.closest('form');

    if (isValid && domForm.checkValidity()) {
      setShowPasswordView(true);
    } else {
      domForm.reportValidity();
    }
  };

  const styles = useMultiStyleConfig('FormAuth', {});

  // Show email confirmation state after passwordless email is sent (or after invalid/expired link with resend)
  if (isPasswordlessEmailSent && !showPasswordView) {
    return (
      <Stack
        className="passwordless-login-stack"
        gap={{base: 4, lg: 8}}
        {...styles.passwordlessLoginStack}
      >
        {isPasswordlessTokenInvalid ? (
          <Alert
            className="form-alert"
            marginBottom={0}
            status="error"
            variant="error"
          >
            <AlertIcon as={AlertErrorIcon} />
            <Stack flex="1" spacing={1}>
              <AlertTitle>
                <FormattedMessage
                  defaultMessage="This login link has expired or is no longer valid. Send a new link to your email to continue."
                  id="login_form.passwordless.token_invalid_announcement"
                />
              </AlertTitle>
            </Stack>
          </Alert>
        ) : (
          <Text {...styles.description} {...styles.descriptionBold} role="alert">
            <FormattedMessage
              defaultMessage="We've sent a secure login link. Check your inbox to continue."
              id="login_form.passwordless.email_sent_announcement"
            />
          </Text>
        )}
        <LoginFields
          email={email}
          form={form}
          handleForgotPasswordClick={handleForgotPasswordClick}
          hidePassword={true}
          isEmailReadOnly={true}
        />
        {!isPasswordlessTokenInvalid && (
          <Text className="passwordless-login-link-sent-hint" {...styles.linkSentHint}>
            <FormattedMessage
              defaultMessage="Can't find the email? Try checking your spam folder."
              id="login_form.passwordless.check_spam_hint"
            />
          </Text>
        )}
        <Stack className="passwordless-login-button-container" {...styles.buttonContainerPasswordlessLogin}>
          <Button
            isLoading={form.formState.isSubmitting}
            type="button"
            {...styles.buttonSubmit}
            onClick={handleResendLink}
          >
            {isPasswordlessTokenInvalid ? (
              <FormattedMessage
                defaultMessage="Send new link"
                id="login_form.button.send_new_link"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Resend login link"
                id="login_form.button.resend_link"
              />
            )}
          </Button>
          {isSocialEnabled && idps.length > 0 && (
            <>
              <Text {...styles.divider}>
                <FormattedMessage
                  defaultMessage="Or continue with"
                  id="login_page.or_continue_with"
                />
              </Text>
              <SocialLogin form={form} idps={idps} />
            </>
          )}
        </Stack>
      </Stack>
    );
  }

  return (
    <>
      {!showPasswordView && (
        <Stack className="passwordless-login-form-stack" gap={0}>
          <LoginFields
            email={email}
            form={form}
            handleForgotPasswordClick={handleForgotPasswordClick}
            hidePassword={true}
            isEmailReadOnly={!!email}
          />
          <Stack {...styles.buttonContainer}>
            <Stack {...styles.buttonGroup}>
              <Button
                isLoading={form.formState.isSubmitting}
                type="submit"
                onClick={() => {
                  handlePasswordlessLoginClick();
                  form.clearErrors('global');
                }}
                {...styles.buttonSubmit}
              >
                <FormattedMessage
                  defaultMessage="Email secure link"
                  id="login_form.button.email_secure_link"
                />
              </Button>
              <Button
                {...styles.buttonOutline}
                onClick={handlePasswordButton}
              >
                <FormattedMessage
                  defaultMessage="Password"
                  id="login_form.button.password"
                />
              </Button>
            </Stack>
            {isSocialEnabled && idps.length > 0 && (
              <>
                <Text {...styles.divider}>
                  <FormattedMessage
                    defaultMessage="Or continue with"
                    id="login_page.or_continue_with"
                  />
                </Text>
                <SocialLogin form={form} idps={idps} />
              </>
            )}
          </Stack>
        </Stack>
      )}
      {showPasswordView && (
        <StandardLogin
          email={email}
          form={form}
          handleBackToLanding={handleBackToLanding}
          handleForgotPasswordClick={handleForgotPasswordClick}
          idps={idps}
          isSocialEnabled={isSocialEnabled}
          setShowPasswordView={setShowPasswordView}
        />
      )}
    </>
  );
};

PasswordlessLogin.propTypes = {
  email: PropTypes.string,
  form: PropTypes.object,
  handleBackToLanding: PropTypes.func,
  handleForgotPasswordClick: PropTypes.func,
  handlePasswordlessLoginClick: PropTypes.func,
  handleResendLink: PropTypes.func,
  idps: PropTypes.arrayOf(PropTypes.string),
  isPasswordlessEmailSent: PropTypes.bool,
  isPasswordlessTokenInvalid: PropTypes.bool,
  isSocialEnabled: PropTypes.bool,
  setLoginType: PropTypes.func,
};

export default PasswordlessLogin;
